/**
 * Мечта (core-loop-v2 §4; план 2d, Task 6).
 *
 * `DreamPicker` — карточка в стиле решения: три мечты из
 * `life.pendingDream`, по одной полноширинной кнопке; тап —
 * `game.chooseDream`, выбор применяется сразу. Пока мечта не выбрана, год
 * не идёт (кнопка «Следующий год» закрыта), поэтому карточка
 * подсвечивается рамкой чернилами с охряной полосой слева (`attention`),
 * как решение, которое ждёт ответа. Без предложения не рисуется.
 *
 * `DreamLine` — строка в шапке экрана жизни: «Мечта: свой дом — 40 %»
 * (`dreamProgress` движка) или «Мечта сбылась: …»; без мечты — пусто.
 * Без полосок прогресса: по дизайн-системе число — это факт в моно.
 */
import type { Dataset } from '@life/data';
import { dreamProgress, type Life } from '@life/engine';
import { t } from '@life/i18n';
import type { Game } from '../state/game';

/** Текст мечты для шапки и досье: прогресс в процентах или «сбылась»; null — мечты нет. */
export function dreamText(life: Life, ds: Dataset): string | null {
  const dream = life.dream;
  if (dream === null) return null;
  const name = t(`dream.${dream.id}`);
  if (dream.achievedAt !== null) return t('ui.dream.done', { dream: name });
  return t('dream.progress', { dream: name, percent: Math.round(dreamProgress(life, ds) * 100) });
}

export function DreamLine({ life, ds }: { life: Life; ds: Dataset }) {
  const text = dreamText(life, ds);
  if (text === null) return null;
  return <span class="dream-line mono">{text}</span>;
}

export function DreamPicker({ game, attention = true }: { game: Game; attention?: boolean }) {
  const life = game.life.value;
  if (life === null || !life.alive) return null;
  const offered = life.pendingDream;
  if (offered === null || offered.length === 0) return null;
  return (
    <section class={attention ? 'decision dream-picker attention' : 'decision dream-picker'} aria-live="polite">
      <div class="eyebrow">{t('ui.dream.eyebrow')}</div>
      <h2 class="t">{t('ui.dream.title')}</h2>
      <p class="hint">{t('ui.dream.lead')}</p>
      <div class="opts">
        {offered.map((id) => (
          <button key={id} type="button" class="btn opt block" onClick={() => game.chooseDream(id)}>
            {t(`dream.${id}`)}
          </button>
        ))}
      </div>
    </section>
  );
}
